
// HomeToastNotification.jsx 
import React, { useState, useEffect } from 'react';
import { CheckCircle, AlertCircle, X, TrendingUp, DollarSign } from 'lucide-react';

const HomeToastNotification = ({ message, type, onClose }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onClose, 300);
    }, 3000);

    // Progress bar animation
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev <= 0) return 0;
        return prev - 1;
      });
    }, 30);

    return () => {
      clearTimeout(timer);
      clearInterval(interval);
    };
  }, [onClose]);

  return (
    <div className={`toast-container ${type} ${isVisible ? 'visible' : 'hidden'}`}>
      <div className="toast-content">
        <div className="toast-icon-container">
          {type === 'success' ? (
            <>
              <CheckCircle className="toast-icon" size={22} />
              <TrendingUp className="toast-mini-icon" size={12} />
            </>
          ) : (
            <>
              <AlertCircle className="toast-icon" size={22} />
              <DollarSign className="toast-mini-icon" size={12} />
            </>
          )}
        </div>
        <div className="toast-message">{message}</div>
        <button className="toast-close" onClick={onClose}>
          <X size={16} />
        </button>
      </div>
      
      <div className="toast-progress-container">
        <div 
          className="toast-progress-bar" 
          style={{ width: `${progress}%` }} 
        />
      </div>

      <style jsx>{`
        .toast-container {
          position: fixed;
          bottom: 24px;
          right: 24px;
          max-width: 380px;
          width: calc(100% - 48px);
          background: rgba(31, 41, 55, 0.9);
          backdrop-filter: blur(10px);
          border-radius: 12px;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
          overflow: hidden;
          transform: translateX(100px);
          opacity: 0;
          transition: transform 0.3s ease, opacity 0.3s ease;
          z-index: 9999;
          border: 1px solid rgba(255, 255, 255, 0.05);
        }
        
        .toast-container.visible {
          transform: translateX(0);
          opacity: 1;
        }
        
        .toast-container.hidden {
          transform: translateX(100px);
          opacity: 0;
        }
        
        .toast-content {
          display: flex;
          align-items: center;
          padding: 16px 20px;
        }
        
        .toast-icon-container {
          position: relative;
          margin-right: 16px;
        }
        
        .toast-icon {
          color: #F9FAFB;
          filter: drop-shadow(0 0 8px rgba(99, 102, 241, 0.4));
        }
        
        .toast-mini-icon {
          position: absolute;
          bottom: -5px;
          right: -5px;
          background: #1F2937;
          border-radius: 50%;
          padding: 2px;
          color: #F9FAFB;
        }
        
        .toast-message {
          flex: 1;
          color: #F9FAFB;
          font-size: 15px;
          font-weight: 500;
          line-height: 1.5;
        }
        
        .toast-close {
          background: transparent;
          border: none;
          color: #9CA3AF;
          cursor: pointer;
          padding: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          transition: all 0.2s ease;
          margin-left: 8px;
        }
        
        .toast-close:hover {
          background: rgba(255, 255, 255, 0.1);
          color: #F9FAFB;
        }
        
        .toast-progress-container {
          width: 100%;
          height: 3px;
          background: rgba(255, 255, 255, 0.1);
          position: relative;
        }
        
        .toast-progress-bar {
          height: 100%;
          transition: width 0.03s linear;
        }
        
        /* Success */
        .toast-container.success {
          border-left: 4px solid #10B981;
        }
        
        .toast-container.success .toast-icon {
          color: #10B981;
        }
        
        .toast-container.success .toast-mini-icon {
          color: #10B981;
        }
        
        .toast-container.success .toast-progress-bar {
          background: linear-gradient(90deg, #059669, #10B981);
        }
        
        /* Error */
        .toast-container.error {
          border-left: 4px solid #EF4444;
        }
        
        .toast-container.error .toast-icon {
          color: #EF4444;
        }
        
        .toast-container.error .toast-mini-icon {
          color: #EF4444;
        }
        
        .toast-container.error .toast-progress-bar {
          background: linear-gradient(90deg, #DC2626, #EF4444);
        }
        
        /* Additional animations for mining theme */
        .toast-container.success::before,
        .toast-container.error::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: radial-gradient(
            circle at top right,
            rgba(99, 102, 241, 0.1) 0%,
            transparent 70%
          );
          z-index: -1;
        }
      `}</style>
    </div>
  );
};

export default HomeToastNotification;